import { lazy } from 'react';

import { dateFormatter, datesComparator } from '@/utils/Dates';
import { priceFormatter, percentageFormatter } from '@/utils/Format';
import { html } from '@/utils/Format';

// dynamic import to avoid warning on npm run build because it is used dynamically on other places
const Chip = lazy(() => import('@mui/material/Chip'));

import { getModalStyle } from '@/utils/Modal';

import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@/components/Button';

import {
    RemoveRedEye as ViewIcon,
    AddCircle as AddIcon,
    Create as EditIcon,
    Delete as DeleteIcon,
    Share as ShareIcon
} from '@mui/icons-material';

import { orderTable } from '@/utils/Order'; 
import { nowByType } from '@/utils/Dates'; 
import { getParameterURL } from '@/utils/URL'; 

import { getNodes, getTableConfig } from '@/utils/Table';

export const formatColumns = (t,user,table,columns,handleDropdown,modal = true,editable = true,
    handleButtonView = null,handleButtonEdit = null,handleButtonDelete = null, handleButtonShare = null,
    openUrlModal = null
) => {

    var formattedTableColumns = [];

    if(columns){

        var columnsKeys = Object.keys(columns);        

        // recover table config if saved
		var tableConfig = getTableConfig(user,table);

        // check if custom order
        let columnsKeysOrdered = columnsKeys;
        if(tableConfig?.columnState && tableConfig?.columnState?.length > 0){
            columnsKeysOrdered = tableConfig.columnState?.filter(state => state?.colId !== 'ag-Grid-SelectionColumn')?.map(state => state?.colId);
        }

        /*
        ---------------
        columnState:
        ---------------
        aggFunc:null
        colId:"ag-Grid-SelectionColumn"
        flex:null
        hide:false
        pinned:null
        pivot:false
        pivotIndex:null
        rowGroup:false
        rowGroupIndex:null
        sort:null
        sortIndex:null
        width:50
        */

        columnsKeysOrdered?.map((column) => {

            let settings = null;
            if(Object.hasOwn(columns,column)){
                settings = columns[column];
            }

            var colId = column;

            // visibility
            let hide = false;
            if(tableConfig?.columnVisibility && Object.hasOwn(tableConfig?.columnVisibility,column)){
            
                if(tableConfig?.columnVisibility[column]){
                	hide = false;
                }
                else{
                	hide = true;
                }
            }
            else{
            	// if the column is not on the config, check visible parameter coming from backend            	
            	if(settings && Object.hasOwn(settings,'visible')){
            		if(settings.visible){
            			hide = false;
            		}
            		else{
            			hide = true;
            		}
            	}
            	else{
            		// show it
                	hide = false;
                }
            }

            // widths
            let width = null;
            if(tableConfig?.columnSize && Object.hasOwn(tableConfig?.columnSize,column)){
            
                if(tableConfig?.columnSize[column]){
                	width = tableConfig?.columnSize[column];
                }
                else{
                	// if null or 0, autowidth
                    width = null;
                }
            }
            else{
            	// if the column is not on the config, autowidth

            	// check if any width from backend, so use it and do not autowidth
            	// if 0 autowidth
            	if(settings && settings?.width){
            		width = settings?.width;
            	}
            	else{
                	width = null;
                }
            }

            // check if adjust to content for the columns
            let autoHeight = false;
            if(tableConfig?.columnAutoHeight && Object.hasOwn(tableConfig?.columnAutoHeight,column)){
                autoHeight = tableConfig?.columnAutoHeight[column] ? true : false;
            }
            else{
                // if the columnAutoHeight is not on the config, do not adjust

                // check if autoheight from backend
                // if 0 do not adjust
                if(settings && Object.hasOwn(settings,'autoheight')){
                    autoHeight = settings['autoheight'] ? true : false;
                }
                else{
                    autoHeight = false; 
                }
            }

            // check sorting            
            let sort = null;
            let sortIndex = null;
            let sortFound = null;
            if(tableConfig?.columnState){
                // search the column
                let columnState = tableConfig.columnState?.filter(state => state?.colId === colId);
                if(columnState?.length > 0){
                    sortFound = columnState[0];                    
                    sort = sortFound?.sort;
                }
            }

            // if sort not found, check if coming from backend
            const validSorts = ['asc','desc'];
            if(!sortFound){                
                if(settings && Object.hasOwn(settings,'sort') && validSorts.includes(settings.sort)){
                    sort = settings.sort;
                }
            }

            // if valid sort, check sortIndex
            if(sort){
                if(sortFound){
                    sortIndex = sortFound?.sortIndex;
                }
                else{
                    if(settings && Object.hasOwn(settings,'sortIndex')){
                        sortIndex = parseInt(settings.sortIndex);
                    }
                }
            }

            // check if pinned            
            let pinned = null;
            let pinnedFound = null;
            if(tableConfig?.columnState){
                // search the column
                let columnState = tableConfig.columnState?.filter(state => state?.colId === colId);
                if(columnState?.length > 0){
                    pinnedFound = columnState[0];                    
                    pinned = pinnedFound?.pinned;
                }
            }

            // if not found, check if coming from backend            
            if(!pinnedFound){                
                if(settings && Object.hasOwn(settings,'pinned')){
                    pinned = settings.pinned;
                }
            }

            // check pivot            
            let pivot = null;
            let pivotIndex = null;
            let pivotFound = null;
            if(tableConfig?.columnState){
                // search the column
                let columnState = tableConfig.columnState?.filter(state => state?.colId === colId);
                if(columnState?.length > 0){
                    pivotFound = columnState[0];                    
                    pivot = pivotFound?.pivot;
                }
            }

            // if not found, check if coming from backend            
            if(!pivotFound){                
                if(settings && Object.hasOwn(settings,'pivot')){
                    pivot = settings.pivot;
                }
            }

            // if valid pivot, check pivotIndex
            if(pivot){
                if(pivotFound){
                    pivotIndex = pivotFound?.pivotIndex;
                }
                else{
                    if(settings && Object.hasOwn(settings,'pivotIndex')){
                        pivotIndex = parseInt(settings.pivotIndex);
                    }
                }
            }

            // check row group            
            let rowGroup = null;
            let rowGroupIndex = null;
            let rowGroupFound = null;
            if(tableConfig?.columnState){
                // search the column
                let columnState = tableConfig.columnState?.filter(state => state?.colId === colId);
                if(columnState?.length > 0){
                    rowGroupFound = columnState[0];                    
                    rowGroup = rowGroupFound?.rowGroup;
                }
            }

            // if not found, check if coming from backend            
            if(!rowGroupFound){                
                if(settings && Object.hasOwn(settings,'rowGroup')){
                    rowGroup = settings.rowGroup;
                }
            }

            // if valid row group, check rowGroupIndex
            if(rowGroup){
                if(rowGroupFound){
                    rowGroupIndex = rowGroupFound?.rowGroupIndex;
                }
                else{
                    if(settings && Object.hasOwn(settings,'rowGroupIndex')){
                        rowGroupIndex = parseInt(settings.rowGroupIndex);
                    }
                }
            }

            // check flex            
            let flex = null;            
            let flexFound = null;
            if(tableConfig?.columnState){
                // search the column
                let columnState = tableConfig.columnState?.filter(state => state?.colId === colId);
                if(columnState?.length > 0){
                    flexFound = columnState[0];                    
                    flex = flexFound?.flex;
                }
            }

            // if not found, check if coming from backend            
            if(!flexFound){                
                if(settings && Object.hasOwn(settings,'flex')){
                    flex = settings.flex;
                }
            }

            const title = t(table+'.fields.'+column);

            //const fieldEditableInline = !modal && editable ? typeof settings?.editable === 'undefined' || settings?.editable === null || settings?.editable === true ? true : false : false;
            const fieldEditable = !settings || typeof settings?.editable === 'undefined' || settings?.editable === null || settings?.editable ? true : false;
            const fieldEditableInline = !modal && editable ? fieldEditable : false;

            // get cells classes
            var columnClassDefault = settings && Object.hasOwn(settings,'classes') ? settings.classes+' ' : '';
            var classesRules = settings && Object.hasOwn(settings,'classesRules') && settings.classesRules ? settings.classesRules : null;

            // https://www.ag-grid.com/javascript-data-grid/column-headers/
            var columnDefinition = {                
                colId: colId,
                field: column, 
                headerName: title,
                headerClass: settings?.headerClasses ? 'table-header '+settings.headerClasses : 'table-header',
                //headerStyle: { color: 'red', 'background-color': 'green' }
                cellClass: params => {
                    return classesRules && Object.hasOwn(classesRules,params?.value) ?
                            columnClassDefault+classesRules[params?.value]
                        :
                            columnClassDefault
                    ;
                },
                //cellStyle: {textAlign: 'center'},                
                hide: hide,
                editable: fieldEditable,                
                sort: sort,
                sortIndex: sortIndex,
                //floatingFilter: true, to show filters above the first row                
                width: width !== null ? parseInt(width) : null,
                suppressSizeToFit: width !== null ? true : false,
                wrapText: autoHeight,
                autoHeight: autoHeight,
                autoHeaderHeight: settings && Object.hasOwn(settings,'autoheightheader') && settings.autoheightheader ? true : false,
                pinned: pinned,
                pivot: pivot,
                pivotIndex: pivotIndex,
                rowGroup: rowGroup,
                rowGroupIndex: rowGroupIndex,
                flex: flex
            };

            var type = settings?.type;
            let names;
            let colors;            
            
            switch(type){

                case 'actions':
                    // add column at the beginning with actions                    
                    //minWidth: 200                        
                    columnDefinition.editable = false;
                    columnDefinition.sortable = false;
                    columnDefinition.filter = false;                    
                    columnDefinition.cellRenderer = (params) => renderActions(editable,settings,params,handleButtonView,handleButtonEdit,handleButtonDelete,handleButtonShare);
                    break;

                case 'id':
                    columnDefinition.editable = false;
                    columnDefinition.cellDataType = 'number';     
                    //columnDefinition.cellStyle = {textAlign: 'left'};
                    //columnDefinition.maxWidth = 100;
                    break;

                case 'html':                    
                    columnDefinition.cellRenderer = renderHtml;
                    columnDefinition.comparator = (valueA, valueB, nodeA, nodeB, isDescending) => sortColumns(column,valueA,valueB,nodeA,nodeB,isDescending,'string');
                    break;

                case 'boolean_checkbox':
                    
                    columnDefinition.cellDataType = 'boolean';
                    
                    columnDefinition.cellStyle = {textAlign: 'right'};

                    // check if custom names instead of no/yes                    
                    names = [t('types.boolean.no'),t('types.boolean.yes')];
                    if(settings && Object.hasOwn(settings,'values')){                                                
                        names = [t(table+'.'+column+'.'+settings.values[0]),t(table+'.'+column+'.'+settings.values[1])];

                        // if custom names, the header is the positive value to understand the meaning of the checkbox
                        columnDefinition.headerName = names[1];
                    }

                    colors = null;
                    if(settings && Object.hasOwn(settings,'colors')){
                        colors = settings.colors;
                    }
                    
                    columnDefinition.cellRenderer = (params) => renderCheckbox(t,params,table,column,names,colors);
                    
                    // to export
                    columnDefinition.valueFormatter = (params) => params?.value ? names[1] : names[0];
                    
                    break;

                case 'boolean':                    
                    
                    columnDefinition.cellDataType = 'boolean';
                    
                    // check if custom names instead of no/yes                    
                    names = [t('types.boolean.no'),t('types.boolean.yes')];
                    if(settings && Object.hasOwn(settings,'values')){
                        names = [t(table+'.'+column+'.'+settings.values[0]),t(table+'.'+column+'.'+settings.values[1])];
                    }

                    columnDefinition.valueFormatter = (params) => params?.value ? names[1] : names[0]; // to export
                    
                    columnDefinition.cellRenderer = (params) => params?.value ? names[1] : names[0];
                    
                    columnDefinition.cellStyle = {textAlign: 'right'};                    
                    
                    break;

                case 'boolean_badge':                    
                    
                    columnDefinition.cellDataType = 'boolean';

                    // check if custom names instead of no/yes                    
                    names = [t('types.boolean.no'),t('types.boolean.yes')];
                    if(settings && Object.hasOwn(settings,'values')){
                        names = [t(table+'.'+column+'.'+settings.values[0]),t(table+'.'+column+'.'+settings.values[1])];                        
                    }
                    
                    columnDefinition.valueFormatter = (params) => params?.value ? names[1] : names[0]; // to export
                    
                    colors = null;
                    if(settings && Object.hasOwn(settings,'colors')){
                        colors = settings.colors;
                    }

                    columnDefinition.cellRenderer = (params) => renderChip(t,params,table,column,names,colors);
                    
                    columnDefinition.cellStyle = {textAlign: 'right'};
                    
                    break;

                case 'datetime':
                case 'datetime_offset':
                case 'date':                
                    columnDefinition.cellDataType = 'dateString';
                    columnDefinition.valueFormatter = (params) => dateFormatter(params,table,column,type === 'datetime' || type === 'datetime_offset' ? true : false,type === 'datetime_offset' ? true : false);
                    columnDefinition.filter = 'agDateColumnFilter'; // default filters
                    columnDefinition.filterParams = {
                        comparator: (date,cellValue) => datesComparator(date,cellValue)
                    };
                    columnDefinition.comparator = (valueA, valueB, nodeA, nodeB, isDescending) => sortColumns(column,valueA,valueB,nodeA,nodeB,isDescending,type);
                    columnDefinition.cellStyle = {textAlign: 'right'};
                    break;

                case 'hour':
                    // it can be used as string to order, etc.
                    columnDefinition.filter = 'agTextColumnFilter';
                    columnDefinition.cellStyle = {textAlign: 'right'};
                    break;

                case 'int':
                case 'float':                    
                    columnDefinition.cellDataType = 'number';
                    columnDefinition.cellStyle = {textAlign: 'right'};
                    //columnDefinition.filter = 'agNumberColumnFilter'; // default filters
                    break;

                case 'price':
                    columnDefinition.cellDataType = 'number';
                    columnDefinition.cellStyle = {textAlign: 'right'};
                    //columnDefinition.filter = 'agNumberColumnFilter'; // default filters
                    columnDefinition.valueFormatter = (params) => priceFormatter(params,table,column);
                    break;

                case 'percentage':
                    columnDefinition.cellDataType = 'number';
                    columnDefinition.cellStyle = {textAlign: 'right'};
                    //columnDefinition.filter = 'agNumberColumnFilter'; // default filters
                    columnDefinition.valueFormatter = (params) => percentageFormatter(params,table,column);
                    break;

                case 'dropdown':
                case 'dropdown_search':
                case 'dropdown_multiple': 

                    // add empty value
                    var emptyOption = {
                        id: null,
                        name: ''
                    };
                    
                    var options = [
                        emptyOption,
                        ...(settings?.values || [])
                    ];

                    // if needs translation                        
                    if(settings?.translate){

                        // add empty value
                        const optionsTranslated = [];
                        options?.map((item) => {                            
                            optionsTranslated.push({
                                id: item?.id,
                                name: 
                                    item?.name && item.name.length > 0 ? 
                                        t(table+'.'+column+'.'+item?.name) === table+'.'+column+'.'+item?.name ?
                                            item.name
                                        :
                                            t(table+'.'+column+'.'+item?.name) 
                                    : 
                                        ''
                            });
                        });
                        options = optionsTranslated;
                    }                   

                    // https://www.ag-grid.com/react-data-grid/provided-cell-editors-select/       
                    // https://www.ag-grid.com/javascript-data-grid/reference-data/#using-the-refdata-property             
                    //columnDefinition.cellEditor = 'agSelectCellEditor';                     
                    //columnDefinition.suppressSizeToFit = true;                    
                    columnDefinition.filter = 'agTextColumnFilter';
                    columnDefinition.comparator = (valueA, valueB, nodeA, nodeB, isDescending) => sortColumns(column,valueA,valueB,nodeA,nodeB,isDescending,type,options);
                           
                    // single dropdown
                    if(type === 'dropdown' || type === 'dropdown_search'){

                        const optionsValues = [];
                        const optionsMapping = {};
                        options?.map((item) => {
                            optionsValues.push(item?.id);
                            optionsMapping[item?.id] = item?.name;
                        });

                        // if we want a custom filter
                        // https://www.ag-grid.com/javascript-data-grid/component-filter/
                        columnDefinition.filterParams = {
                            textMatcher: (params) => dropdownFilter(type,params,t,table,column,optionsMapping)
                        };

                        // it should not be necessary because aggrid does it by default but using it because if not
                        // when copying to clipboard, it does not copy the text
                        // because valueFormatter does not exist
                        columnDefinition.valueFormatter = (params) => {
                            
                            if(params.value == null || params.value === '' || typeof params.value === 'undefined'){
                                return '';
                            }
    
                            const refData = params?.colDef?.refData;
                            
                            return refData && Object?.hasOwn(refData,params?.value) ? refData[params.value] : params?.value;
                        };

                        if(type === 'dropdown'){
                            
                            columnDefinition.cellEditor = 'agSelectCellEditor';

                            columnDefinition.cellEditorParams = {
                                values: optionsValues
                            };  

                            columnDefinition.refData = optionsMapping;           

                            // to use generic badge
                            // columnDefinition.cellRenderer = (params) => renderDropdown(t,params,optionsMapping,table,column);

                            // if using colors, show badge                                                        
                            if(settings && Object.hasOwn(settings,'colors') && Object.keys(settings.colors).length > 0){
                                columnDefinition.cellRenderer = (params) => renderDropdown(t,params,optionsMapping,table,column,settings.colors);
                            }
                        }
                        else{

                            // necessary to avoid an issue when saving with no value, submitting, receiving error message
                            // because some missing values and opening again the dropdown and selecting a value
                            columnDefinition.refData = optionsMapping;

                            columnDefinition.editable = false;
                            
                            // add click option on the cell to open the modal and change the value, if the cell is editable inline
                            if(fieldEditableInline){
                                //columnDefinition.onCellDoubleClicked = (params) => handleDropdownSearch(t,params,options,optionsMapping,table,column,handleDropdown);
                                columnDefinition.onCellDoubleClicked = (params) => handleDropdown(params,options,column);
                            }
                            
                            colors = null;
                            if(settings && Object.hasOwn(settings,'colors') && Object.keys(settings.colors).length > 0){
                                colors = settings.colors;
                            }
                            columnDefinition.cellRenderer = (params) => renderDropdownSearch(t,params,options,optionsMapping,table,column/*,handleDropdown*/,colors);
                        }                        
                    }
                    else{

                        // if we want a custom filter
                        // https://www.ag-grid.com/javascript-data-grid/component-filter/
                        columnDefinition.filterParams = {
                            textMatcher: (params) => dropdownFilter(type,params,t,table,column,options)
                        };

                        //'dropdown_multiple'
                        columnDefinition.editable = false;  

                        // add click option on the cell to open the modal and change the value, if the cell is editable inline
                        if(fieldEditableInline){
                            //columnDefinition.onCellDoubleClicked = (params) => handleDropdownMultiple(t,params,options,table,column,handleDropdown);
                            columnDefinition.onCellDoubleClicked = (params) => handleDropdown(params,options,column);
                        }

                        columnDefinition.cellRenderer = (params) => renderDropdownMultiple(t,params,options,table,column/*,handleDropdown*/);

                        // to export
                        columnDefinition.valueFormatter = (params) => Array.isArray(params?.value) && params?.value?.map(item => item?.name).join(', ');
                    }

                    break;

                case 'file':

                    // array of elements with
                    // {name,path}
                    columnDefinition.cellDataType = 'text';
                    columnDefinition.filter = 'agTextColumnFilter';

                    // to have case insensitive and do not regard accents
                    //columnDefinition.comparator = (valueA, valueB, nodeA, nodeB, isDescending) => sortColumns(column,valueA,valueB,nodeA,nodeB,isDescending,'string');

                    columnDefinition.cellRenderer = params => {

                        if(params?.value){
                            
                            let files;

                            // check if array or directly the link
                            if(Array.isArray(params.value)){
                                files = params.value;
                            }
                            else{
                                // if directly the link, create element
                                files = [{                                    
                                    path: params.value
                                }];
                            }
                            
                            if(files?.length > 0){

                                var render = [];
                                files.forEach((item) => {

                                    if(item?.path && item.path?.length > 0){

                                        var linkText = item?.name ? item.name : item?.type && t('types.'+item.type) !== 'types.'+item.type ? t('types.'+item.type) : t('types.file');

                                        render.push((!settings?.target || settings.target === 'modal') && openUrlModal ?
                                            <a className='cursor-pointer' onClick={() => openUrlModal(item?.path,true,item?.type,item?.mime,item?.name)}>{linkText}</a>
                                        :
                                            <a href={item?.path} target={settings.target}>
                                                {linkText}
                                            </a>                                  
                                        );
                                    }
                                })

                                return (
                                    render && render?.map((renderLink,index) => (
                                        <span key={table+'_'+column+'_'+params?.node?.rowIndex+'_'+index}>
                                            {renderLink}{index < render?.length-1 ? ' / ' : '' }
                                        </span>
                                    ))
                                );
                            }
                        }
                        else{
                            return '';
                        }
                    }

                    // to export
                    columnDefinition.valueFormatter = params => {
                        
                        if(params?.value){

                            let files;

                            // check if array or directly the link
                            if(Array.isArray(params.value)){
                                files = params.value;
                            }
                            else{
                                // if directly the link, create element
                                files = [{                                    
                                    path: params.value
                                }];
                            }

                            const paths = [];
                            files?.forEach((item) => {
                                paths.push(item?.path);
                            });

                            return paths.join(' / ');
                        }
                        else{
                            return '';
                        }
                    }
                    break;

                // text
                // textlong
                // phone
                // email
                default:

                    columnDefinition.filter = 'agTextColumnFilter'; // default filters

                    // to have case insensitive and do not regard accents
                    columnDefinition.comparator = (valueA, valueB, nodeA, nodeB, isDescending) => sortColumns(column,valueA,valueB,nodeA,nodeB,isDescending,'string');

                    /* default is false
                    columnDefinition.filterParams = {
                        caseSensitive: false
                    };
                    */       

                    if(type === 'link' || type === 'linkshort' || type === 'email' || type === 'phone'){                        

                        columnDefinition.cellRenderer = params => {

                            var linkText = type === 'linkshort' ? t('types.link') : settings && Object.hasOwn(settings,'value') ? t(table+'.'+column+'.'+settings.value) : params?.value;
                            
                            if(params?.value?.length > 0){
                                return (
                                
                                    (!settings?.target || settings.target === 'modal') && openUrlModal ?
                                        <a className='cursor-pointer' onClick={() => openUrlModal(params?.value,Object?.hasOwn(settings,'file') ? settings?.file : null)}>{linkText}</a>
                                    :
                                        <a href={`${type === 'email' ? 'mailto:' : type === 'phone' ? 'tel:' : '' }${params?.value}`} target={settings?.target}>
                                            {linkText}
                                        </a>
                                )
                            }
                        }
                    }
                    else{
                        // if text has values, translate 
                        // it makes sense if the field is not editable

                        // if translation or colors
                        if(settings && Object.hasOwn(settings,'values') && settings.values){

                            names = settings.values;

                            // to export
                            columnDefinition.valueFormatter = (params) => params?.value && Object.hasOwn(names,params?.value) ? t(table+'.'+column+'.'+names[params?.value]) : params?.value;

                            // to render the cell
                            columnDefinition.cellRenderer = (params) => params?.value && Object.hasOwn(names,params?.value) ? t(table+'.'+column+'.'+names[params?.value]) : params?.value;
                        }
                    }       
                    break;
            }
            
            formattedTableColumns.push(columnDefinition);
        });
    }

    return formattedTableColumns;
}

// to render the actions buttons
const renderActions = (editable,settings,params,handleButtonView,handleButtonEdit,handleButtonDelete,handleButtonShare) => {

    var options = [];
    if(Object.hasOwn(settings,'options')){
        options = settings?.options;
    }

    return (
        <div className=''>
            {
                options?.includes('view') &&
                <ViewIcon /*fontSize='large'*/ onClick={() => handleButtonView(params)} className='action-button'/>
            }
            {
                options?.includes('edit') && editable &&
                <EditIcon /*fontSize='large'*/ onClick={() => handleButtonEdit(params,'row','edit')} className='action-button'/>
            }
            {
                options?.includes('delete') && editable &&
                <DeleteIcon sx={{color: '#FF0000'}} /*fontSize='large'*/ onClick={() => handleButtonDelete(params)} className='action-button'/>                
            }
            {
                options?.includes('share') &&
                <ShareIcon /*fontSize='large'*/ onClick={() => handleButtonShare(params)} className='action-button'/>
            }
        </div>
    );
}

// to detect the action to be executed
/*
const executeAction = (params) => {

    // get data-action from button
    const action = params.event.target.dataset.action;
    //const action = params.event.target.className;

    switch(action){

        case 'delete':
            //params.api.applyTransaction({remove:[params.node.data]});
            break;
    }
}
*/

// https://blog.ag-grid.com/full-row-editing-ag-grid-committing-changes-button-click
// https://www.ag-grid.com/react-data-grid/data-update-transactions/
/*
const addActions = (params,t) => {
    
    // with onClick it does not work because it auto executes
    return (
        <button 
            data-action='delete' 
            class="button"
        >
            {t("actions.delete")}
        </button>
    )
}
*/

// to order the columns
const sortColumns = (column,valueA, valueB, nodeA, nodeB, isDescending, type = 'string', options = null) => {
    return orderTable(column,valueA,valueB,nodeA,nodeB,isDescending,type,options);
}

// to render html on the cells
const renderHtml = (params) => {

    try {
        return html(params.value);
        //return <div dangerouslySetInnerHTML={{__html: html(params.value)}}></div>
    }
    catch(exception){
        if(exception){
            //console.error(exception);
            return '';
        }
    }
}

// to render a tick or red cross
const renderCheckbox = (t,params,table,column,names,colors = null) => {    

    try {

        //const field = params.colDef.field;
        const value = params.value;

        return(            
            <div className='mt-2'>
            {
                value ?
                    <Typography color={colors && colors[1] ? colors[1] : 'success'}>V</Typography>
                :
                    <Typography color={colors && colors[0] ? colors[0] : 'error'}>X</Typography>
            }
            </div>
        );
    }
    catch(exception){
        if(exception){
            return '';
        }
    }
}

// to render a chip depending on the value
const renderChip = (t,params,table,column,names,colors = null) => {    

    try {

        //const field = params.colDef.field;
        const value = params.value;

        let label;
        let color;

        //if(field === 'actiu'){

            if(value){
                label = names[1];
                color = colors && colors[1] ? colors[1] : "success";
            }
            else{
                label = names[0];
                color = colors && colors[0] ? colors[0] : "error";
            }

            return(
                <span key={table+'_'+column+'_'+value}>                    
                    <Chip
                        label={label} 
                        color={color}
                        onClick={() => {}}
                    />                    
                </span>
            );
        //}
        //else{
        //    return value;
        //}
    }
    catch(exception){
        if(exception){
            return '';
        }
    }
}

// to render a dropdown if single, not multiple
const renderDropdown = (t,params,options,table,column,colors = null) => {

    try {

        const { sxChip, sxChipNoColor } = getModalStyle();

        //const field = params.colDef.field;
        const value = params.value;
        var node = params?.node;
        const rowIndex = node?.rowIndex;

        if(value){
            return (  
                <span key={table+'_'+column+'_'+rowIndex+'_'+value}>
                    <Chip   
                        sx={colors ? sxChipNoColor : sxChip}
                        label={options[value] ? options[value] : 'ID: '+value+' ('+t('error.notvalid')+')'}
                        color={colors && Object.hasOwn(colors,value) ? colors[value] : 'info'}                        
                        onClick={() => {}} 
                    />                    
                </span>
            );
        }
        else{
            return '';
        }
    }
    catch(exception){
        if(exception){
            return '';
        }
    }
}

// to open modal when clicking on dropdown multiple
/*
const handleDropdownMultiple = (t,params,options,table,column,handleDropdown) => {
    const values = params?.value;
    handleDropdown('multiple',params,table,column,values,options);
}
*/

// to render a dropdown if multiple
const renderDropdownMultiple = (t,params,options,table,column/*,handleDropdown*/) => {

    try {

        const { sxChip } = getModalStyle();

        //const field = params.colDef.field;        
        const values = params?.value;
        const node = params?.node;
        const rowIndex = node?.rowIndex;

        //if(table === 'notifications' && field === 'roles'){
        
            return (
                values?.length > 0 ?
                    values.map((item) => (
                        <span key={table+'_'+column+'_'+rowIndex+'_'+item?.id}>
                            <Chip                 
                                sx={sxChip}            
                                label={item?.name}                                
                                // do not add onClick and add to the entire cell
                                // onClick={(e) => handleDropdown('multiple',e,params,table,column,values,options)} 
                                onClick={() => {}}
                            />
                        </span>
                    ))
                :
                    <span key={table+'_'+column+'_'+rowIndex+'_empty'}>
                        <Chip                         
                            label={t('error.empty')}
                            color='error'
                            /*
                            sx={{
                                marginX:'2px',
                                backgroundColor:'rgb(255,0,0,0.7)',
                                color:'#FFFFFF'
                            }}*/
                            // do not add onClick and add to the entire cell
                            //onClick={(e) => handleDropdown('multiple',e,params,table,column,values,options)}                             
                            onClick={() => {}}
                        />
                    </span>
            );

        //}
        //else{
        //    return value;
        //}
    }
    catch(exception){
        if(exception){
            return '';
        }
    }
}

/*
// to open modal when clicking on dropdown search
const handleDropdownSearch = (t,params,options,optionsMapping,table,column,handleDropdown) => {

    try {

        //const { sxChip, sxChipNoColor } = getModalStyle();

        //const field = params.colDef.field;        
        const value = params?.value;
        let name;
        let valueObject;

        // prepare value as an object
        if(value){
            name = optionsMapping[value] ? optionsMapping[value] : 'ID: '+value+' ('+t('error.notvalid')+')';
            valueObject = {id:value,name:name};
        }
        else{
            // new row
            name = t('error.empty');
            valueObject = {id:null,name:''};
        }

        handleDropdown('search',params,table,column,valueObject,options);
    }
    catch(exception){
        if(exception){
            return null;
        }
    }
}
*/
// to render a dropdown search
const renderDropdownSearch = (t,params,options,optionsMapping,table,column/*,handleDropdown*/,colors = null) => {

    try {

        //const field = params.colDef.field;        
        const value = params?.value;
        const node = params?.node;
        const rowIndex = node?.rowIndex;

        let name;
        let color;

        // if using colors
        if(colors){

            const { sxChipNoColor } = getModalStyle();

            if(value){

                name = optionsMapping[value] ? optionsMapping[value] : 'ID: '+value+' ('+t('error.notvalid')+')';
                color = optionsMapping[value] ? colors && Object.hasOwn(colors,value) ? colors[value] : 'info' : 'error';

                return (  
                    <span key={table+'_'+column+'_'+rowIndex+'_'+value}>                
                        <Chip   
                            sx={sxChipNoColor}
                            label={name}
                            color={color}
                            onClick={() => {}} 
                        />                    
                    </span>
                );
            }
            else{
                return '';
            }
        }
        else{

            // prepare value as an object
            if(value){
                name = optionsMapping[value] ? optionsMapping[value] : 'ID: '+value+' ('+t('error.notvalid')+')'
                return name;
            }
            else{            
                /*
                // empty
                var name = t('error.empty');
                //var valueObject = {id:null,name:''};
                var render = <Chip                 
                        label={name}
                        sx={value ? {backgroundColor:'rgb(129,0,0,0.2)'} : {}}
                        color={value ? '' : 'error'}
                        // do not add onClick and add to the entire cell
                        //onClick={(e) => handleDropdown('search',e,params,table,column,valueObject,options)} 
                        onClick={() => {}}
                    />;
                */
                return '';
            }            
        }
    }
    catch(exception){
        if(exception){
            return '';
        }
    }
}
    
// to filter on the dropdown
const dropdownFilter = (type,params,t,table,column,options) => {

    const filterOption = params?.filterOption;

    let cellValues;
    
    if(!type || type === 'dropdown_multiple'){
        cellValues = params?.data[column];
    }
    else{
        // dropdown_search

        const cellValue = params?.value;
        //var cellValue = params?.data[column];

        // create array of 1 element to re-use the code for multiple
        const cellText = options[cellValue];
        cellValues = [{id:cellValue,name:cellText}];
    }

    var filterText = params?.filterText;

    if(filterText == null){
        return false;
    }

    var resul = false;

    // check if cell without value and we type "Without value" or the proper translation
    if(!cellValues || cellValues.length === 0){
        const value = t('error.empty');
        resul = compareText(value,filterText,filterOption);
    }
    else{

        cellValues && cellValues.map((item) => {

            // if this if at the end of the map, it does NOT exit from the map
            if(resul){
                return;
            }

            var value = item?.name;
            if(!value || value === 'undefined'){
                value = '';
            }

            resul = compareText(value,filterText,filterOption);

        });
    }

    return resul;
}

// to compare 2 strings
const compareText = (value,filterText,filterOption) => {

    value = value.toLowerCase();
    filterText = filterText.toLowerCase();

    var resul = false;

    switch(filterOption){
        
        case 'contains':
            resul = value.indexOf(filterText) >= 0;                
            break;
        
        case 'notContains':
            resul = value.indexOf(filterText) < 0;
            break;
        
        case 'equals':
            resul = value === filterText;
            break
        
        case 'notEqual':
            resul = value != filterText;
            break;
        
        case 'startsWith':
            resul = value.indexOf(filterText) === 0;
            break;
        
        case 'endsWith':
            var index = value.lastIndexOf(filterText);
            resul = index >= 0 && index === (value.length - filterText.length);
            break;
        
        default:
            // should never happen
            //console.warn('invalid filter type ' + filterOption);
            //resul = false;
            break;
    }

    return resul;
}

// format values when received, to format them for the ag grid
export const formatValuesReceived = (t,table,columns,data) => {

    if(columns && data && data.length > 0){

        var columnsKeys = Object.keys(columns);        
        
        columnsKeys?.map((column) => {

            var settings = columns[column];
            var type = settings?.type;            

            data?.map((row,index) => {

                // if the row has the column
                if(Object?.hasOwn(row,column)){

                    let value;
                
                    switch(type){

                        // 0/1 => false/true
                        case 'boolean':
                        case 'boolean_badge':
                        case 'boolean_checkbox':
                            
                            value = row[column];

                            if(value === null || typeof value === 'undefined' || value === 0 || value === "0" || value === false){
                                value = false;
                            }
                            else{
                                value = true;
                            }

                            row[column] = value;                           
                            break;

                        // if dropdown multiple and needs translation
                        case 'dropdown_multiple':
                            
                            if(settings?.translate){

                                var values = row[column];
                                
                                if(values && values.length > 0){                                
                                    values.map((value,valueIndex) => {
                                        values[valueIndex].name = value?.name && value.name.length > 0 ? t(table+'.'+column+'.'+value.name) === table+'.'+column+'.'+value.name ? value.name : t(table+'.'+column+'.'+value.name) : '';
                                    });   
                                }
                                
                                row[column] = values;                            
                            }
                            break;

                        // string to int
                        case 'dropdown':
                        case 'dropdown_search':
                            if(row[column] !== null && typeof row[column] !== 'undefined' && row[column] !== ''){
                                row[column] = parseInt(row[column]);
                            }
                            break;

                        case 'int':                        
                            value = row[column];
                            if(value !== null){
                                row[column] = parseInt(value);
                            }
                            break;

                        case 'float':                        
                        case 'price':
                        case 'percentage':
                            value = row[column];
                            if(value !== null){
                                row[column] = parseFloat(value);
                            }
                            break;

                        case 'file':
                            if(!value){
                                value = [];
                            }
                            break;
                    }

                    data[index] = row;
                }
            });
        });
    }

    return data;
}

// format values back when sending to server to be edited
export const formatValuesToSend = (t,table,columns,data) => {

    var dataFormatted = null;

    if(columns){

        var columnsKeys = Object.keys(columns);

        // dataFormatted = {...data};
        // remove the unnecessary fields. keep 'id' because it is always used to update        
        dataFormatted = Object.keys(data).reduce((acc, key) => {          
            
            // if id send always
            if(key === 'id'){
                acc[key] = data[key];
            }
            else{
                // do not send 'actions'
                if(columnsKeys.includes(key)){
                    var settings = columns[key];
                    var type = settings?.type;
                    if(!type || type !== 'actions'){
                        acc[key] = data[key];
                    }
                }
            }
            
            return acc;

        }, {});
        
        if(dataFormatted && Object.keys(dataFormatted)?.length > 0){
            
            Object.keys(dataFormatted)?.forEach((column) => {

                var settings = columns[column];
                var value = dataFormatted[column];

                var type = settings?.type;
                let newValue;
            
                switch(type){

                    // false/true => 0/1
                    case 'boolean':
                    case 'boolean_badge':
                    case 'boolean_checkbox':

                        if(value === null || typeof value === 'undefined' || value === 0 || value === "0" || value === false){
                            value = 0;
                        }
                        else{
                            value = 1;
                        }   
                        break;

                    case 'datetime':
                    case 'date':     
                    case 'hour':               
                    case 'datetime_offset':                        
                        
                        if(value === '0000-00-00 00:00:00' || value === '0000-00-00' || value === false || value === null){
                            value = '';
                        }
                        break;

                    // change to csv
                    case 'dropdown_multiple':
                        
                        newValue = [];
                        value?.forEach((item) => {
                            newValue.push(item?.id);                      
                        });
                        value = newValue.join(',');
                        break;

                    // string to int
                    case 'dropdown':
                    case 'dropdown_search':
                        if(value !== null && typeof value !== 'undefined' && value !== ''){
                            value = parseInt(value);
                        }
                        break;

                    case 'file':
                        // if files, if delete = 1, remove from data to send
                        value = formatFilesToSend(value);                        
                        break;

                    case 'int':
                    case 'id':
                        if(value !== null && typeof value !== 'undefined'){
                            value = parseInt(value);
                        }
                        break;
                }

                // change null to empty to send. if not it sends 'null'
                if(value === null || typeof value === 'undefined'){
                    value = '';
                }

                dataFormatted[column] = value;
            });
        }
    }

    return dataFormatted;
}

// to remove the files that are flagged as deleted
export const formatFilesToSend = (files) => {

    let result = null;
    const newFiles = [];
    
    files && files?.length > 0 && files?.forEach((item) => {
        if(!item?.delete){
            delete item.delete;
            newFiles.push(item);
        }
    });

    if(newFiles && newFiles?.length > 0){
        result = newFiles;
    }

    return result;
}

// to get default values when adding a new row
export const getDefaultValue = (api, table, router, field, config) => {

    var defaultValue = null;
    var configDefault = null;

    // check if some values from url parameter. it rules over config
    const valueParam = getParameterURL(router,field);    

    // if existing, even if empty, apply it
    if(valueParam !== null){

        switch(table){

            case 'tickets':

                // in table tickets, ticket_category_id change 'planifications' for proper id
                // same for ticket_subcategory_id
                if(field === 'ticket_category_id' || field === 'ticket_subcategory_id'){

                    if(config?.values){
                        
                        const filter = config.values?.filter((value) => value?.menu === valueParam);
                        if(filter && filter?.length > 0){
                            const elem = filter.pop();
                            configDefault = parseInt(elem?.id);
                        }

                    }

                }

                break;            
        
            default:
                configDefault = valueParam;
                break;
        }
    }
    else{
        if(config && Object.hasOwn(config,'default')){
            configDefault = config.default;
        }
    }

    // depending on the field type
    switch(config?.type){

        case 'boolean':
        case 'boolean_badge':
        case 'boolean_checkbox':

            // check if default value
            if(configDefault && configDefault === 1){
                defaultValue = true;
            }
            else{
                defaultValue = false;
            }
            break;

        case 'datetime':
        case 'date':
        case 'hour':
        case 'datetime_offset':
            
            if(configDefault && configDefault === 'now'){
                defaultValue = nowByType(config.type,true);
            }
            else{
                defaultValue = null;
            }
            break;

        case 'int':
            // if max, get max value and add 1
            if(configDefault && configDefault === 'max'){                
                
                const nodes = getNodes(api);
                
                var max = 0;
                nodes && nodes?.forEach((node) => {
                    
                    var data = node?.data;
                    
                    if(Object?.hasOwn(data,field)){

                        var intValue = parseInt(data[field]);
                        if(intValue > max){
                            max = intValue;
                        }
                    }
                });

                defaultValue = max+1;
            }
            else{
                defaultValue = configDefault;
            }
            break;

        default:
            defaultValue = configDefault;            
            break;
    }

    return defaultValue;
}

// check if column editable
export const columnIsEditable = (colDef) => {

    if(
        colDef?.editable && 
        colDef?.cellDataType !== 'dropdown' &&
        colDef?.cellDataType !== 'dropdown_search' &&
        colDef?.cellDataType !== 'dropdown_multiple' &&
        colDef?.cellDataType !== 'boolean' &&
        colDef?.cellDataType !== 'boolean_badge' &&
        colDef?.cellDataType !== 'boolean_checkbox'
    ){
        return true;
    }
    
    return false;    
}

// get the key of the first editable column
export const getEditableColumn = (apiTable, column = null) => {

    // if column has value, it means that we want to focus on it
    // but maybe it is not possible because it is not editable or dropdown, so move to closest one
    var columnEditable = null;
    const cols = apiTable?.getColumns();

    if(!column){    
        
        cols?.map((col) => {

            // return at the end of the map, it does not work
            if(columnEditable !== null){
                return;
            }

            var colDef = col?.colDef;

            // column editable and not dropdown             
            if(columnIsEditable(colDef)){
                columnEditable = colDef.field;                  
            }
        });
    }
    else{

        // loop to find current col config
        var found = null;
        var firstEditableColumn = null;
        var closestPreviousEditableColumn = null;        
        cols?.map((col,index) => {

            // return at the end of the map, it does not work
            if(columnEditable !== null){
                return;
            }

            var colDef = col?.colDef;

            if(colDef?.field === column){

                found = index;

                if(columnIsEditable(colDef)){
                    columnEditable = index;
                }
            }

            // while column not found, save the lastest editable column prior to it
            // once found, get the next editable column
            if(!columnEditable && columnIsEditable(colDef)){
                
                if(found === null){

                    closestPreviousEditableColumn = index; 

                    // save first editable column
                    if(firstEditableColumn === null){
                        firstEditableColumn = index;
                    }                   
                }
                else{
                    // if column found but not editable, return the next editable column
                    columnEditable = index;
                }
            }
        });

        // check if columnEditable not found, so get the closest editable column
        // of if column not found, the first editable column
        if(found === null){
            // if column not found, get first editable column
            columnEditable = firstEditableColumn;
        }
        else{

            // if columnEditable is null, it means column found but not editable
            // so next after column none editable => get closest previous one
            if(columnEditable === null){                
                columnEditable = closestPreviousEditableColumn;
            }
            else{
                // if columnEditable not null, it can be the column itself or the following editable one
                if(columnEditable === found){
                    // nothing to do, column found and editable
                }
                else{
                    // check closest previous and after
                    const distancePrevious = found-closestPreviousEditableColumn;
                    const distanceAfter = columnEditable-found;
                    
                    if(distancePrevious <= distanceAfter){
                        columnEditable = closestPreviousEditableColumn;
                    }
                }
            }
        }

        // change index for key
        if(columnEditable !== null){
            columnEditable = cols[columnEditable]?.colDef?.field;
        }

    }

    // if column editable is null, use 'id'
    if(columnEditable === null){
        columnEditable = 'id';
    }

    return columnEditable;
}

// to get the minimum table size to fit columns or not
export const checkCanFit = (table, columnsKeys) => {

    const gridElement = document.querySelector('#table-grid');
    const gridWidth = gridElement?.offsetWidth;
    const columnsLen = columnsKeys?.length;
    const columnWidth = gridWidth/columnsLen;
    const minColumnWidth = 100;

    if(columnWidth >= minColumnWidth){
        return true;
    }
    else{
        return false;
    }
}

// check dependencies among columns
export const checkDependencies = (fieldName,fieldValue,data,fields) => {

    let valueToApply;

    const booleanTypes = ['boolean','boolean_badge','boolean_checkbox'];
    const dropdownTypes = ['dropdown','dropdown_multiple','dropdown_search'];

    // check if there are dependant values
    if(
        fields && Object.hasOwn(fields,fieldName) && 
        fields[fieldName] && Object.hasOwn(fields[fieldName],'dependants') &&
        fields[fieldName]['dependants']
    ){

        Object.keys(fields[fieldName]['dependants'])?.forEach((dependant) => {   

            const values = fields[fieldName]['dependants'][dependant];   

            const type = fields[fieldName]?.type;

            if(booleanTypes?.includes(type) && values?.length === 2){

                valueToApply = values[fieldValue ? 1 : 0];

                // if date and value is 'now', get current date
                if(valueToApply === 'now'){
                    valueToApply = nowByType(fields[dependant]?.type,true);
                }

                data[dependant] = valueToApply;                
            }
            else{
                
                // dependant can be:
                // 'field' => [1 => 'value1', 3 => 'value3']
                // it means if parent is 1, dependant is value1, etc.   
                // so dependant is not another dropdown

                // or dependant can be

                // 'field' => 'parent'
                // it means it has a parent field indicating the parent value
                // [{id:10,name:'a',parent:2}], etc.
                // so dependant is a dropdown
                
                if(booleanTypes?.includes(type) || dropdownTypes?.includes(type)){

                    if(values){

                        if(Object.hasOwn(fields,dependant)){

                            const dependantType = fields[dependant]?.type;

                            if(dropdownTypes?.includes(dependantType)){
                                
                                //if(typeof values !== 'object' && !Array.isArray(values)){
                              
                                // get all values
                                const valuesall = fields[dependant]?.valuesall;                            

                                if(valuesall && valuesall?.length > 0){

                                    // usually 'parent'
                                    const fieldParent = values;

                                    // check if boolean has a value or if not take default
                                    var realFieldValue = fieldValue;
                                    if(booleanTypes?.includes(type)){

                                        if(fieldValue === null){

                                            realFieldValue = 0;

                                            if(Object?.hasOwn(fields[fieldName],'default')){
                                                realFieldValue = fields[fieldName].default;
                                            }
                                        }

                                        // boolean to int 0/1
                                        realFieldValue = realFieldValue ? 1 : 0;
                                    }

                                    const newOptions = valuesall?.filter((item) => item[fieldParent] === realFieldValue);

                                    // clean previous value on the dependant
                                    data[dependant] = null;

                                    // if only one option, update the dependant
                                    if(newOptions && newOptions?.length === 1){                                        

                                        if(dependantType === 'dropdown' || dependantType === 'dropdown_search'){
                                            const newValue = newOptions[0];
                                            data[dependant] = newValue?.id;
                                        }
                                        else{
                                            // dropdown_multiple
                                            data[dependant] = newOptions;
                                        }                                        
                                    }

                                    // update options to choose
                                    fields[dependant].values = newOptions;     
                                }            
                            }
                            else{
                                // when has 'field' => [1 => 'value1', 3 => 'value3']
                                if(Object?.keys(values) && Object?.keys(values)?.length > 0){ 

                                    if(Object.hasOwn(values,fieldValue)){
                                        data[dependant] = values[fieldValue];
                                    }
                                }                                
                            }   

                        }

                    }
                }
                else{

                    if(values && Object.hasOwn(values,fieldValue)){
                        
                        valueToApply = values[fieldValue];

                        // if date and value is 'now', get current date
                        if(valueToApply === 'now'){
                            valueToApply = nowByType(fields[dependant]?.type,true);
                        }

                        data[dependant] = valueToApply;
                    }    
                }
            }
        });
    }

    return data;
}

// check if dropdown and dependant on another dropdown because we need to change the possible options
// if data is null, we are adding. if not, we are editing
export const checkDependenciesDropdown = (fields, data = null) => {

    //const booleanTypes = ['boolean','boolean_badge','boolean_checkbox'];
    const dropdownTypes = ['dropdown','dropdown_multiple','dropdown_search'];

    // check if there are dependant values
    if(fields){

        Object.keys(fields)?.forEach((fieldName) => {

            if(Object.hasOwn(fields[fieldName],'dependants') && fields[fieldName]['dependants']){

                var fieldValue = null;
                if(data && Object.hasOwn(data,fieldName)){
                    fieldValue = data[fieldName];
                }

                //let valueToApply;
                Object.keys(fields[fieldName]['dependants'])?.forEach((dependant) => {   

                    var values = fields[fieldName]['dependants'][dependant];   

                    var type = fields[fieldName]?.type;

                    switch(type){

                        case 'dropdown':
                        case 'dropdown_search':
                        case 'dropdown_multiple':

                            // dependant can be:
                            // 'field' => [1 => 'value1', 3 => 'value3']
                            // it means if parent is 1, dependant is value1, etc.   
                            // so dependant is not another dropdown

                            // 'field' => 'parent'
                            // it means it has a parent field indicating the parent value
                            // [{id:10,name:'a',parent:2}], etc.
                            // so dependant is a dropdown

                            // when depends on field
                            // [{id:10,name:'a',parent:2}], etc.
                            if(values){

                                // get dependant options
                                if(Object.hasOwn(fields,dependant)){   

                                    if(
                                        fields[dependant]?.type === 'dropdown' || 
                                        fields[dependant]?.type === 'dropdown_search' || 
                                        fields[dependant]?.type === 'dropdown_multiple'
                                    ){                           
                                    
                                        //if(typeof values !== 'object' && !Array.isArray(values)){
                                      
                                        // get all values
                                        const valuesall = fields[dependant]?.valuesall;                            

                                        if(valuesall && valuesall?.length > 0){

                                            // usually 'parent'
                                            const fieldParent = values;

                                            const newOptions = valuesall?.filter((item) => item[fieldParent] === fieldValue);

                                            fields[dependant].values = newOptions;       
                                        }            
                                    }                            
                                }
                            }

                            break;

                        case 'boolean':
                        case 'boolean_badge':
                        case 'boolean_checkbox':
                            // 'field' => 'parent'
                            // it means it has a parent field indicating the parent value
                            // parent can be 0 or 1 because it is boolean
                            
                            // dependant is a dropdown

                            // when depends on field
                            // [{id:10,name:'a',parent:0}]
                            // [{id:10,name:'a',parent:1}]
                            // get dependant options
                            if(values){

                                if(Object.hasOwn(fields,dependant)){   

                                    if(dropdownTypes?.includes(fields[dependant]?.type)){       
                                      
                                        // get all values
                                        const valuesall = fields[dependant]?.valuesall;                            

                                        if(valuesall && valuesall?.length > 0){

                                            // usually 'parent'
                                            const fieldParent = values;

                                            // check if boolean has a value or if not take default
                                            var realFieldValue = fieldValue;
                                            if(fieldValue === null){

                                                realFieldValue = 0;

                                                if(Object?.hasOwn(fields[fieldName],'default')){
                                                    realFieldValue = fields[fieldName].default;
                                                }
                                            }

                                            realFieldValue = realFieldValue ? 1 : 0;

                                            const newOptions = valuesall?.filter((item) => item[fieldParent] === realFieldValue);

                                            fields[dependant].values = newOptions;       
                                        }            
                                    }                            
                                }
                            }
                            break;
                    }
                });
            }
        });
    }
}